import { zodResolver } from '@hookform/resolvers/zod'
import Badge from '@repo/ui/badge'
import DataTable, { Column } from '@repo/ui/data-table'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getTripDate } from '../../../api/trip-dates/get-trip-date'
import { TransportTypeEnum, TripTypeEnum } from '../../../lib/types/enums/trip/trip-type.enum'
import { TripStepTypeEnum } from '../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { TripBookingProperties } from '../../../lib/types/models/trip-booking/properties.trip-booking'
import { TripDateProperties } from '../../../lib/types/models/trip-booking/properties.trip-date'
import { TripBookingTypes } from '../../../lib/types/models/trip-booking/trip-booking'
import { DispatchIndividualInput } from '../../../lib/types/models/trip-booking/trip-date-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledRadioInput from '../../elements/inputs/controlled-radio-input'
import { ItemNotFound } from '../../elements/item-not-found'
import LoadingScreen from '../../elements/loading/loading-screen'
import ValidationButtons from '../../elements/validation-buttons'
import { TripBookingDetailsBadge } from '../trip-bookings/trip-booking.ui'

type DispatchIndividualsFormProps = BaseFormProps<DispatchIndividualInput, true> & {
  tripDateId: number
  individualsCount?: number
}

const formSchema: ZodType<DispatchIndividualInput> = z.object({
  tripBookingId: createNumberSchema('Réservation', { isInteger: true, isPositive: true }),
  oneWayTripBookingStepId: createNumberSchema('Étape de voyage', {
    isInteger: true,
    isPositive: true,
    optional: true
  }),
  returnTripBookingStepId: createNumberSchema('Étape de voyage', {
    isInteger: true,
    isPositive: true,
    optional: true
  })
})

export const DispatchIndividualsForm = ({
  tripDateId,
  defaultValues,
  individualsCount,
  ...validationButtonProps
}: DispatchIndividualsFormProps) => {
  const form = useForm<DispatchIndividualInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: tripDate, isPending: isPendingTripDate } = useQuery({
    queryKey: ['trip-date', tripDateId],
    queryFn: async () => getTripDate(tripDateId)
  })

  const { tripBookingId, oneWayTripBookingStepId, returnTripBookingStepId } = form.watch()
  const tripBookings = TripDateProperties.getTripBookingsByDirectionality(tripDate)

  function handleAllocatedPlacesChange(tripBooking: TripBookingTypes.Partial) {
    const { directionality } = tripBooking

    const tripStepDirectionality =
      directionality === TransportTypeEnum.RETURN
        ? TripStepTypeEnum.OUTBOUND
        : TripStepTypeEnum.INBOUND

    form.setValue('tripBookingId', tripBooking.id)

    if (directionality !== TransportTypeEnum.ROUND_TRIP) {
      form.setValue('oneWayTripBookingStepId', undefined)
      form.setValue('returnTripBookingStepId', undefined)
    }

    if (
      directionality === TransportTypeEnum.ONE_WAY ||
      directionality === TransportTypeEnum.RETURN
    ) {
      // get the trip steps of the trip booking
      const mainTripSteps = TripBookingProperties.getTripStepsByDirectionality(
        tripStepDirectionality,
        tripBooking
      )
      const secondaryTripSteps = TripBookingProperties.getTripStepsByDirectionality(
        tripStepDirectionality,
        tripBooking.secondaryReservation
      )

      const groupedTripSteps =
        TripBookingProperties.sortTripStepsById([
          ...(mainTripSteps ?? []),
          ...(secondaryTripSteps ?? [])
        ]) ?? []

      if (groupedTripSteps.length === 0) return

      if (directionality === TransportTypeEnum.ONE_WAY)
        form.setValue('oneWayTripBookingStepId', groupedTripSteps[0].id)
      else form.setValue('returnTripBookingStepId', groupedTripSteps[0].id)
    } else if (directionality === TransportTypeEnum.ROUND_TRIP) {
      const roundTripOneWayTripSteps = TripBookingProperties.getTripStepsByDirectionality(
        TripStepTypeEnum.INBOUND,
        tripBooking
      )
      const roundTripReturnTripSteps = TripBookingProperties.getTripStepsByDirectionality(
        TripStepTypeEnum.OUTBOUND,
        tripBooking
      )

      form.setValue('oneWayTripBookingStepId', roundTripOneWayTripSteps?.[0]?.id)
      form.setValue(
        'returnTripBookingStepId',
        roundTripReturnTripSteps?.[roundTripReturnTripSteps?.length - 1]?.id
      )
    }

    form.trigger()
  }

  const columns: Column<TripBookingTypes.Partial>[] = [
    {
      accessorKey: 'id',
      header: '',
      cell: ({ row }) => {
        return (
          <FormField
            control={form.control}
            name={'tripBookingId'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    required
                    checked={row.original.id === tripBookingId}
                    onCheck={() => handleAllocatedPlacesChange(row.original)}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )
      }
    },
    {
      accessorKey: 'directionality',
      header: 'Réservations',
      cell: ({ row }) => {
        if (!tripDate) return null
        return (
          <div className={'py-2 flex flex-col gap-2'}>
            <TripBookingDetailsBadge.Controlled
              directionality={tripDate.directionality}
              detailOptions={{
                count:
                  tripDate?.trip.type === TripTypeEnum.PRIMARY
                    ? ['allocated-dispatched']
                    : ['reserved-dispatched']
              }}
              tripBooking={row.original}
              oneWayTripBookingStepId={oneWayTripBookingStepId}
              returnTripBookingStepId={returnTripBookingStepId}
              onTripStepChange={(data) => form.reset({ ...data, tripBookingId: row.original.id })}
            />
          </div>
        )
      }
    }
  ]

  if (isPendingTripDate) return <LoadingScreen />
  if (!tripDate || !tripBookings)
    return <ItemNotFound title={"La date de voyage que vous cherchez n'existe pas"} />

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={'p-4 flex flex-col gap-3'}>
          {individualsCount && (
            <Badge variant={'grey'} text={`${individualsCount} sélectionnés`} />
          )}

          <DataTable<TripBookingTypes.Partial>
            paginated={false}
            columns={columns}
            hideSelectAllCheckbox
            data={tripBookings}
            rowCount={tripBookings.length}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}
